
import { Injectable } from '@angular/core';
import { Directory, Filesystem, WriteFileResult } from '@capacitor/filesystem';
import { TranslateUtilityService } from './translation-utility.service';
import { MEErrorService } from 'src/app/common/error/me-error.service';
import { ToastService } from './toast.service';
import { StringUtilityService } from './string-utility.service';
import { logger } from 'src/app/common/services/logging/logging.service';
import { PlatformService } from './platform.service';

@Injectable({
    providedIn: 'root'
})
export class FileSystemService {

    constructor(
        private translate: TranslateUtilityService,
        private srvError: MEErrorService,
        private srvToast: ToastService
    ) { }

    public async downloadFile(filename: string, type: string, bytedata: ArrayBuffer, key: string = 'LABEL_DOWNLOAD_SUCCESS') {
        let blob = new Blob([bytedata], { type });
        this.downloadBlob(filename, blob, key);
    }

    public async downloadBlob(filename: string, blob: Blob, key: string = 'LABEL_DOWNLOAD_SUCCESS') {
        if (PlatformService.isBrowser()) {
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename!;
            link.click();
        } else {
            this.writeFile(filename, blob, Directory.Documents, key);
        }
    }

    public async saveTempFile(path: string, data: Blob | string): Promise<string> {
        try {
            logger.debug('📁 Saving temp file:', path);
            logger.debug('📊 Data type:', typeof data);

            // Handle different data types for iOS compatibility
            let fileData: string;

            if (data instanceof Blob) {
                // Convert Blob to base64 string
                logger.debug('🔄 Converting Blob to base64...');
                fileData = await StringUtilityService.blobToBase64(data);
            } else if (typeof data === 'string') {
                if (data.startsWith('data:')) {
                    // Extract base64 data from data URL
                    logger.debug('🔄 Extracting base64 from data URL...');
                    const base64Index = data.indexOf(',');
                    if (base64Index !== -1) {
                        fileData = data.substring(base64Index + 1);
                    } else {
                        throw new Error('Invalid data URL format');
                    }
                } else {
                    // Assume it's already base64
                    fileData = data;
                }
            } else {
                throw new Error('Unsupported data type');
            }

            logger.debug('💾 Writing file with base64 data, length:', fileData.length);

            // Use base64 data for iOS compatibility
            const outputPath: WriteFileResult = await Filesystem.writeFile({
                path,
                data: fileData,
                directory: Directory.Cache // Use Cache directory for temp files
            });

            logger.debug('✅ File saved successfully:', outputPath.uri);
            return outputPath.uri;

        } catch (error) {
            logger.error('❌ Error saving temp file:', error);

            // Fallback: Try with different approach for iOS
            if (PlatformService.isIOS() && typeof data === 'string' && data.startsWith('data:')) {
                logger.debug('🔄 Trying iOS fallback approach...');
                return await this.saveTempFileIOSFallback(path, data);
            }

            throw error;
        }
    }

    /**
     * iOS-specific fallback for saving data URLs
     */
    private async saveTempFileIOSFallback(path: string, dataUrl: string): Promise<string> {
        try {
            logger.debug('📱 Using iOS fallback for data URL...');

            // Extract MIME type and base64 data
            const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
            if (!matches || matches.length !== 3) {
                throw new Error('Invalid data URL format');
            }

            const mimeType = matches[1];
            const base64Data = matches[2];

            logger.debug('📄 MIME type:', mimeType);
            logger.debug('📊 Base64 data length:', base64Data.length);

            // Write using just the base64 data
            const outputPath: WriteFileResult = await Filesystem.writeFile({
                path,
                data: base64Data,
                directory: Directory.Cache
            });

            logger.debug('✅ iOS fallback successful:', outputPath.uri);
            return outputPath.uri;

        } catch (error) {
            logger.error('❌ iOS fallback failed:', error);
            throw error;
        }
    }

    // /**
    //  * Write and Share (Skip share if subject is given)
    //  *  Default will trigger Sharing of the file
    //  *  https://mobilearth.atlassian.net/browse/M25-1228
    //  */
    // private async writeAndShare(path: string, data: Blob | string, key: string) {

    //     if (typeof data !== 'string') {
    //         data = await StringUtilityService.blobToBase64(data!);
    //     }

    //     let outputPath: string;
    //     try {
    //         outputPath = await this.saveTempFile(path, data);
    //     }
    //     catch (err) {
    //         logger.error('Unable to write file', err);
    //         this.srvError.showDialog("LABEL_PERMISSION_DEINED", this.translate.instant("LABEL_DOWNLOAD_FAILED"));
    //         return;
    //     }

    //     try {
    //         await Share.share({
    //             text: this.translate.instant('DOWNLOAD_SHARE_TEXT'),
    //             files: [outputPath]
    //         });

    //         logger.debug(`Wrote file to ${outputPath}`);
    //         this.srvToast.showToast(this.translate.instant(key));
    //     }
    //     catch (err) {
    //         logger.error('Unable to share file', err);
    //     }
    //     finally {
    //         await Filesystem.deleteFile({ path, directory: Directory.Cache });
    //     }
    // }

    private async writeFile(path: string, data: Blob | string, directory: Directory, key: string) {

        if (typeof data !== 'string') {
            data = await StringUtilityService.blobToBase64(data!);
        }

        try {
            path = await this.lookupNewFilename(path, directory);

            let outputPath: WriteFileResult = await Filesystem.writeFile({ path, data, directory });
            logger.debug(`Wrote file to ${outputPath.uri}`);
            this.srvToast.showToast(this.translate.instant(key));
        }
        catch (err) {
            logger.error('Unable to write file', err);
            this.srvError.showDialog("LABEL_PERMISSION_DEINED", this.translate.instant("LABEL_DOWNLOAD_FAILED"));
        }

        this.srvToast.showToast(this.translate.instant(key));
    }

    /**
     * Lookup a unique filename in the directory 
     */ 
    private async lookupNewFilename(fileName: string, directory: Directory): Promise<string> {

        let fileNameWithoutExt = fileName?.split('.').slice(0, -1).join('.');
        let fileExt = fileName?.split('.').pop();
        let fileIndex = 0;
        let newFileName = fileName;
        let isFileExist = false;
        do {
            newFileName = fileNameWithoutExt + (fileIndex > 0 ? `_${fileIndex}` : '') + (fileExt ? `.${fileExt}` : '');
            isFileExist = await Filesystem.stat({ path: newFileName, directory }).then(() => true).catch(() => false);
            fileIndex++;
        }
        while (isFileExist);

        logger.debug(`File ${fileName} already exists, using ${newFileName} instead`);
        return newFileName;
    }

}